import { apiService } from "@services/ApiService"; // Adjust the import based on your project structure
import { Comment } from "@shared/components/EditorJs/comments/interfaces"; // Adjust the import based on your project structure
import axios from "axios";
import { apiBaseUrl } from "@utils/baseValues";

class CommentService {
  // Add a new comment
  static addComment = async (
    projectId: string,
    reportId: string,
    commentData: Comment
  ) => {
    try {
      const response = await apiService.post(
        `/api/projects/comments/${projectId}/report/${reportId}/add/`,
        commentData
      );
      return response?.data;
    } catch (error) {
      console.error("Error adding comment:", error);
      throw error;
    }
  };

  // Reply to a comment
  static replyToComment = async (
    projectId: number,
    reportId: number,
    commentId: number,
    replyData: Comment
  ) => {
    try {
      const response = await apiService.post(
        `/api/projects/comments/${projectId}/report/${reportId}/reply/${commentId}/`,
        replyData
      );
      return response?.data;
    } catch (error) {
      console.error("Error replying to comment:", error);
      throw error;
    }
  };

  // Edit a comment
  static editComment = async (
    projectId: number,
    reportId: number,
    commentId: number,
    updatedCommentData: Partial<Comment>
  ) => {
    try {
      const response = await apiService.patch(
        `/api/projects/comments/${projectId}/report/${reportId}/edit/${commentId}/`,
        updatedCommentData
      );
      return response?.data;
    } catch (error) {
      console.error("Error editing comment:", error);
      throw error;
    }
  };

  // Remove a comment
  static removeComment = async (
    projectId: number,
    reportId: number,
    commentId: number
  ) => {
    try {
      const response = await apiService.delete(
        `/api/projects/comments/${projectId}/report/${reportId}/remove/${commentId}/`,
        undefined,
        undefined,
        undefined,
        "Comment"
      );
      return response;
    } catch (error) {
      console.error("Error removing comment:", error);
      throw error;
    }
  };

  // Remove comment silently (without error handling)
  static removeCommentSilently = async (
    projectId: string,
    reportId: string,
    commentId: string
  ) => {
    try {
      const response = await axios.delete(
        `${apiBaseUrl}/projects/comments/${projectId}/report/${reportId}/remove/${commentId}`
      );
      return response;
    } catch (error) {
      console.error("Error removing comment:", error);
      throw error;
    }
  };

  static nestComments = (flatComments: Comment[]) => {
    const commentMap = new Map<number, Comment>();
    const rootComments: Comment[] = [];

    // Initialize map with all comments and add empty replies array
    flatComments.forEach((comment) => {
      comment.replies = [];
      commentMap.set(comment.id, comment);
    });

    // Nest replies under their parent
    flatComments.forEach((comment) => {
      if (comment.parent_id !== null) {
        const parent = commentMap.get(comment.parent_id);
        if (parent) {
          parent.replies!.push(comment);
        }
      } else {
        rootComments.push(comment);
      }
    });

    return rootComments;
  };

  // Get all comments for a report
  static getComments = async (projectId: string, reportId: string) => {
    try {
      const response = await apiService.get(
        `projects/comments/${projectId}/report/${reportId}`
      );

      const comments = response?.data;

      const nestedComments = CommentService.nestComments(comments);
      return nestedComments;
    } catch (error) {
      console.error("Error fetching comments:", error);
      throw error;
    }
  };
}

export default CommentService;
